// Where to send a user after a successful login or signup. Two entry
// points: a `?redirectTo=...` query parameter on /login or /register
// (used by email links and "soft" callers like a Bid Now button), and
// sessionStorage which `ProtectedRoute` populates when bouncing a
// logged-out user away from an authenticated inner page.

const KEY = "postLoginRedirect";

// A path is safe to navigate to client-side if it stays inside this
// origin and doesn't loop back into the auth pages.
//
//   - must be a string starting with a single `/`
//   - must NOT start with `//` — browsers interpret protocol-relative
//     URLs as external, which would be an open-redirect footgun
//   - must not point at /login or /register (would round-trip back here)
export const isSafeRedirectPath = (path: unknown): path is string => {
  if (typeof path !== "string") return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.startsWith("/login") || path.startsWith("/register")) return false;
  return true;
};

// Reads `redirectTo` from the current URL's query string. Returns null
// if the param is missing or fails the safety check.
export const getRedirectFromUrl = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("redirectTo");
    if (!raw) return null;
    return isSafeRedirectPath(raw) ? raw : null;
  } catch {
    return null;
  }
};

// Persists a `?redirectTo=` URL param into sessionStorage so it survives
// the register → login → success-page navigation chain. Safe to call on
// every render of the auth pages; no-op if the param is absent.
export const captureRedirectFromUrl = (): void => {
  const fromUrl = getRedirectFromUrl();
  if (!fromUrl) return;
  try {
    sessionStorage.setItem(KEY, fromUrl);
  } catch {}
};

// Used by ProtectedRoute to stash the inner page a logged-out user was
// trying to reach before being bounced to /login.
export const setPostLoginRedirect = (path: string): void => {
  if (!isSafeRedirectPath(path)) return;
  try {
    sessionStorage.setItem(KEY, path);
  } catch {}
};

// Stash the page the user is currently on (path + query) before a manual
// bounce to /login that doesn't go through ProtectedRoute — e.g. a "Bid
// Now"/watchlist button on a public page, or the 401 interceptor. This is
// what lets a shared/email link to a public page (like /details/:id) send
// the user back there after they authenticate. No-op for unsafe paths
// (already on /login, etc.).
export const setRedirectToCurrentLocation = (): void => {
  try {
    setPostLoginRedirect(
      `${window.location.pathname}${window.location.search || ""}`,
    );
  } catch {}
};

// Returns the saved redirect target, clears it, and falls back to the
// caller-provided default. Priority is URL query > sessionStorage so a
// fresh email link always wins over a stale stashed value.
export const consumePostLoginRedirect = (fallback = "/"): string => {
  const fromUrl = getRedirectFromUrl();
  let saved: string | null = null;
  try {
    saved = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {}
  if (fromUrl) return fromUrl;
  if (saved && isSafeRedirectPath(saved)) return saved;
  return fallback;
};
